import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import industries, { industryApproach, industryFeatures } from "../data/industries";
import Seo from "../seo/Seo";
import pageMeta from "../data/pageMeta";

import "../styles/industries.css";

const reveal = {
  hidden: {
    opacity: 0,
    y: 28,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const heroStats = [
  {
    value: "6",
    label: "Focus industries",
  },
  {
    value: "1",
    label: "Growth partner",
  },
  {
    value: "AI",
    label: "Automation ready",
  },
];

const heroHighlights = ["Market-specific", "Conversion-led", "Trust first"];

const featureCards = industryFeatures.slice(0, 6);

function IndustriesPage() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(industries[0].slug);

  const activeIndustry = useMemo(
    () => industries.find((industry) => industry.slug === activeId) || industries[0],
    [activeId]
  );

  const scrollToIndustryGrid = () => {
    document.getElementById("industries-grid")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const scrollToRoadmap = () => {
    document.getElementById("industries-roadmap")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const openIndustry = (slug) => {
    setActiveId(slug);
    document.getElementById(`industry-${slug}`)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <>
      <Seo title={pageMeta.industries.title} description={pageMeta.industries.description} />

      <main className="industries-page">
        <section className="industries-hero">
          <div className="industries-hero__grid" />
          <div className="industries-hero__glow industries-hero__glow--left" />
          <div className="industries-hero__glow industries-hero__glow--right" />

          <div className="container industries-hero__layout">
            <motion.div
              variants={reveal}
              initial="hidden"
              animate="visible"
              className="industries-hero__copy"
            >
              <span className="section-chip">Industries</span>
              <p className="industries-hero__eyebrow">Built for the market you want to win</p>

              <h1 className="industries-hero__title">
                Every sector gets a tailored growth engine, not a template.
              </h1>

              <p className="industries-hero__lead">
                We shape the story, the system and the experience around the reality of each
                market, so the brand feels specific, credible and premium from the first
                interaction.
              </p>

              <div className="industries-hero__pills" aria-label="Industry highlights">
                {heroHighlights.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>

              <div className="industries-hero__actions">
                <button type="button" className="button-primary" onClick={scrollToIndustryGrid}>
                  Explore sectors
                  <ArrowRight size={18} />
                </button>

                <button type="button" className="button-secondary" onClick={() => navigate("/contact")}>
                  Book a strategy call
                </button>
              </div>

              <div className="industries-hero__stats">
                {heroStats.map((stat) => (
                  <div key={stat.label} className="hero-stat glass-card">
                    <strong>{stat.value}</strong>
                    <span>{stat.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <div className="industries-hero__atlas">
              <motion.aside className="industries-hero__rail glass-panel">
                <div className="industries-hero__rail-copy">
                  <span className="panel-label">Industry switcher</span>
                  <h3>Jump into a sector instantly.</h3>
                  <p>
                    Different markets need different signals. This rail keeps the selection
                    fast, obvious and easy to trust.
                  </p>
                </div>

                <div className="industries-hero__selector">
                  {industries.map((industry) => {
                    const Icon = industry.icon;

                    return (
                      <button
                        key={industry.slug}
                        type="button"
                        className={activeId === industry.slug ? "industries-hero__selector-item active" : "industries-hero__selector-item"}
                        onClick={() => setActiveId(industry.slug)}
                      >
                        <span className="industries-hero__selector-icon">
                          <Icon size={16} />
                        </span>

                        <span className="industries-hero__selector-copy">
                          <strong>{industry.name}</strong>
                          <span>{industry.badge}</span>
                        </span>

                        <ChevronRight size={16} />
                      </button>
                    );
                  })}
                </div>
              </motion.aside>

              <motion.aside className="industries-hero__panel glass-panel">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndustry.slug}
                    initial={{
                      opacity: 0,
                      y: 20,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    exit={{
                      opacity: 0,
                      y: -16,
                    }}
                    transition={{
                      duration: 0.45,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="industries-hero__panel-inner"
                  >
                    <div className="industries-hero__panel-top">
                      <div>
                        <span className="panel-label">Market atlas</span>
                        <h3>{activeIndustry.name}</h3>
                      </div>

                      <div className="industries-hero__pill">{activeIndustry.badge}</div>
                    </div>

                    <div className="industries-hero__media-shell">
                      <img
                        className="industries-hero__image"
                        src={activeIndustry.image}
                        alt={activeIndustry.name}
                      />

                      <div className="industries-hero__stats-stack">
                        {activeIndustry.stats.map((stat) => (
                          <div key={stat.label} className="industries-hero__stat glass-card">
                            <strong>{stat.value}</strong>
                            <span>{stat.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="industries-hero__panel-copy">
                      <h4>{activeIndustry.headline}</h4>
                      <p>{activeIndustry.description}</p>

                      <div className="industries-hero__focus">
                        {activeIndustry.focusPoints.map((point) => (
                          <span key={point}>{point}</span>
                        ))}
                      </div>

                      <div className="industries-hero__actions industries-hero__actions--panel">
                        <button type="button" className="button-primary" onClick={scrollToRoadmap}>
                          View growth path
                          <ArrowRight size={18} />
                        </button>

                        <button type="button" className="button-secondary" onClick={() => navigate("/services")}>
                          See services
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </motion.aside>
            </div>
          </div>
        </section>

        <section className="industries-proof">
          <div className="container industries-proof__grid">
            {featureCards.map((feature) => {
              const Icon = feature.icon;

              return (
                <motion.article
                  key={feature.title}
                  variants={reveal}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{
                    once: true,
                    amount: 0.3,
                  }}
                  className="proof-card glass-card"
                >
                  <div className="proof-card__icon">
                    <Icon size={18} />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>Premium experience tuned to a specific market signal.</p>
                </motion.article>
              );
            })}
          </div>
        </section>

        <section className="industries-grid" id="industries-grid">
          <div className="container">
            <motion.div
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.3,
              }}
              className="section-heading section-heading--center"
            >
              <span className="section-chip">Sector coverage</span>
              <h2>Built around real industry behaviour.</h2>
              <p>
                Every card combines positioning, user experience and performance strategy so
                each market gets the right message at the right moment.
              </p>
            </motion.div>

            <div className="industries-grid__cards">
              {industries.map((industry) => {
                const Icon = industry.icon;

                return (
                  <motion.article
                    key={industry.slug}
                    id={`industry-${industry.slug}`}
                    variants={reveal}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                      once: true,
                      amount: 0.18,
                    }}
                    className="industry-card glass-card"
                  >
                    <div className="industry-card__top">
                      <div className="industry-card__icon">
                        <Icon size={20} />
                      </div>
                      <span className="industry-card__badge">{industry.badge}</span>
                    </div>

                    <div className="industry-card__media">
                      <img src={industry.image} alt={industry.name} />
                    </div>

                    <div className="industry-card__content">
                      <h3>{industry.name}</h3>
                      <p>{industry.headline}</p>

                      <div className="industry-card__stats">
                        {industry.stats.map((stat) => (
                          <div key={stat.label} className="industry-card__stat">
                            <strong>{stat.value}</strong>
                            <span>{stat.label}</span>
                          </div>
                        ))}
                      </div>

                      <div className="industry-card__focus">
                        {industry.focusPoints.slice(0, 3).map((item) => (
                          <span key={item}>{item}</span>
                        ))}
                      </div>
                    </div>

                    <div className="industry-card__footer">
                      <button type="button" onClick={() => openIndustry(industry.slug)}>
                        Explore market
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="industries-roadmap" id="industries-roadmap">
          <div className="container industries-roadmap__layout">
            <motion.div
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.3,
              }}
              className="industries-roadmap__copy"
            >
              <span className="section-chip">Growth path</span>
              <h2>A premium process that adapts to the market.</h2>
              <p>
                We align on the audience, sharpen the positioning and then build the systems
                that turn attention into measurable demand.
              </p>

              <div className="industries-roadmap__note glass-card">
                <Sparkles size={18} />
                <span>When the market changes, the system stays clear.</span>
              </div>
            </motion.div>

            <div className="industries-roadmap__steps">
              {industryApproach.map((item) => (
                <motion.div
                  key={item.step}
                  initial={{
                    opacity: 0,
                    y: 24,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    duration: 0.6,
                  }}
                  className="roadmap-step glass-card"
                >
                  <span className="roadmap-step__index">{item.step}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </motion.div>
              ))}

              <div className="roadmap-step roadmap-step--accent glass-card">
                <div className="roadmap-step__header">
                  <BarChart3 size={20} />
                  <h3>Outcome design</h3>
                </div>
                <p>
                  We connect the right services to the right audience so the whole journey
                  feels specific and the results stay measurable.
                </p>
                <div className="roadmap-step__tags">
                  {activeIndustry.services.slice(0, 4).map((service) => (
                    <span key={service}>{service}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="industries-cta">
          <div className="container">
            <motion.div
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.3,
              }}
              className="cta-panel glass-panel"
            >
              <div className="cta-panel__copy">
                <span className="section-chip">Why EkritFly</span>
                <h2>One partner. Many industries. One premium standard.</h2>
                <p>
                  We do not sell isolated services. We design connected systems that combine
                  strategy, branding, technology and performance marketing into one measurable
                  engine.
                </p>
              </div>

              <div className="cta-panel__actions">
                <button type="button" className="button-primary" onClick={() => navigate("/contact")}>
                  Start your project
                  <ArrowRight size={18} />
                </button>

                <button type="button" className="button-secondary" onClick={() => navigate("/services")}>
                  Explore services
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}

export default IndustriesPage;
